/**
 * Analytics. Pro and up.
 *
 * The prototype's page (design/PROTOTYPE-SPEC.md §11) had five sections. On
 * 11 October 2026 Emmanuel asked for it to be "comprehensive, exhaustive but
 * useful", and approved a drawn proposal; the page is now built around the
 * newer `insights` block from the API, with target progress and promotions
 * carried over. docs/DECISIONS.md records what was added and what was dropped.
 *
 * ## Every panel answers one question
 *
 * Where did the stock money go, what is about to go out of date, what was
 * thrown away and why, what gets used, what sits still, and how long will each
 * product last. A panel that could not be put as a question a clinic owner
 * would ask was left out.
 *
 * ## Nothing is worked out in the browser
 *
 * Every figure arrives computed by `packages/core`. This file formats and
 * draws. The one period switch refetches the page; it does not re-slice data
 * already loaded, so the charts and the figures beside them cannot disagree.
 *
 * ## Target progress appears only where there is a target to progress towards
 *
 * It reports Targets data, and Targets is Premium. On Pro the API sends null
 * and the section is simply absent: not locked, not teased.
 *
 * ## Empty states say what to do, not that there is nothing
 *
 * A new clinic sees this page with no history at all. Each panel says what has
 * to happen for a number to appear, because on this page an empty state is
 * almost always a new account rather than a broken one.
 */
import { useEffect, useState } from 'react';
import { PlanRequiredError, getAnalytics, type AnalyticsResponse, type Me } from '../lib/api.js';
import { PlanLocked } from './PlanLocked.js';
import { SHARE_LIMIT } from '@inventory-tracker/core';
import type {
  CoverStatus,
  ProductShare,
  InsightPeriod,
  Plan,
  Role as CoreRole,
  StockInsights,
} from '@inventory-tracker/core';
import { c, chart, mono, segTab, segTrack } from '../theme.js';
import {
  AreaLine,
  ColumnBars,
  GroupedBars,
  MiniBars,
  RankBars,
  Spark,
  SplitBar,
  WeekdayBars,
} from './charts.js';
import { cardStyle, formatDate, money, moneyRounded } from './format.js';

interface Props {
  me: Me;
  refreshToken: number;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "2026-07" → "July 2026". */
function monthName(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

/** "2026-07" → "jul". */
function shortMonth(key: string): string {
  return MONTHS[Number(key.slice(5, 7)) - 1] ?? key;
}

/** "2026-07-13" → "13 jul". */
function shortDay(date: string): string {
  return `${Number(date.slice(8, 10))} ${shortMonth(date)}`;
}

/**
 * A hover box's list of products: "Lidocaine: 3 units, £19.50", largest first,
 * then "and 2 more". This is what answers "written off WHAT?" on a chart.
 */
function shareLines(shares: ProductShare[], limit: number = SHARE_LIMIT): string[] {
  const shown = shares.slice(0, limit).map(
    (x) => `${x.productName}: ${x.units} unit${x.units === 1 ? '' : 's'}, ${money(x.pence)}`,
  );
  return shares.length > limit ? [...shown, `and ${shares.length - limit} more`] : shown;
}

/** Whole pounds for a chart axis: "£1,400". */
const axisPounds = (pence: number) => `£${Math.round(pence / 100).toLocaleString('en-GB')}`;

const PERIODS: { days: InsightPeriod; label: string; phrase: string }[] = [
  { days: 30, label: '30 days', phrase: 'last 30 days' },
  { days: 90, label: '90 days', phrase: 'last 90 days' },
  { days: 365, label: '12 months', phrase: 'last 12 months' },
];

const COVER_TAG: Record<CoverStatus, { label: string; bg: string; fg: string; bar: string }> = {
  out: { label: 'Out of stock', bg: c.danger, fg: c.white, bar: c.danger },
  order_now: { label: 'Order now', bg: c.danger, fg: c.white, bar: c.danger },
  order_soon: { label: 'Order soon', bg: c.amberBg, fg: c.amberText, bar: chart.amber },
  ok: { label: 'Fine', bg: c.okBg, fg: c.okText, bar: chart.green },
  not_moving: { label: 'Not moving', bg: c.well, fg: c.muted2, bar: chart.grey },
};

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px 8px 0',
  fontSize: 12.5,
  color: c.faint,
  fontWeight: 400,
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${c.border}`,
};
const td: React.CSSProperties = {
  padding: '10px 12px 10px 0',
  fontSize: 14.5,
  fontVariantNumeric: 'tabular-nums',
  verticalAlign: 'middle',
};
const emptyNote: React.CSSProperties = {
  fontSize: 14,
  color: c.muted2,
  margin: '12px 0 0',
  lineHeight: 1.6,
};

export function AnalyticsPage({ me, refreshToken }: Props) {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [locked, setLocked] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [days, setDays] = useState<InsightPeriod>(90);

  useEffect(() => {
    // A slower answer for the period just left must not overwrite the one for
    // the period now chosen (DECISIONS.md, "Every keystroke firing a request
    // needs sequencing" — the same race, with three buttons instead of keys).
    let current = true;
    getAnalytics(days)
      .then((res) => {
        if (current) setData(res);
      })
      .catch((e: unknown) => {
        if (!current) return;
        if (e instanceof PlanRequiredError) {
          setLocked(e.requiredPlan);
          return;
        }
        setError(e instanceof Error ? e.message : 'Could not load your analytics');
      });
    return () => {
      current = false;
    };
  }, [days, refreshToken]);

  if (locked) {
    return (
      <PlanLocked
        requiredPlan={locked}
        cycle={me.subscription.cycle}
        role={me.user.role as CoreRole}
        title="See where your money actually goes"
        blurb="Where your stock money went each month, how long every product will last at the rate you use it, what is about to go out of date, what you threw away and why, and what sits on the shelf without moving."
      />
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          background: c.dangerBg,
          border: `1px solid ${c.dangerBorder}`,
          borderRadius: 12,
          padding: '12px 16px',
          fontSize: 13.5,
          color: c.danger,
        }}
      >
        {error}
      </div>
    );
  }

  if (!data) return <p style={{ fontSize: 13.5, color: c.muted2 }}>Loading…</p>;

  const { stock, promotions, insights } = data;
  const period = PERIODS.find((p) => p.days === insights.days) ?? PERIODS[1]!;
  // Shown while a newly chosen period is on its way, so the figures on screen
  // are never labelled with a period they do not belong to.
  const waiting = days !== insights.days;
  const { totals, months } = insights;
  const monthLabels = months.map((m) => shortMonth(m.monthKey));
  const monthNames = months.map((m) => monthName(m.monthKey));
  const hasFlow = months.some(
    (m) => m.boughtPence || m.usedPence || m.soldPence || m.writtenOffPence,
  );
  const expiryTotal =
    insights.expiry.expiredPence + insights.expiry.months.reduce((n, m) => n + m.valuePence, 0);
  const wasteMax = Math.max(...insights.waste.byReason.map((r) => r.pence), 1);
  const hasUse = insights.mostUsed.length > 0;
  const retailSold = insights.retail.units > 0;
  const splitColors = [chart.green, chart.navy, chart.amber, chart.orange, chart.grey];

  return (
    <div data-tour="analytics" style={{ opacity: waiting ? 0.55 : 1, transition: 'opacity .15s' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 14, color: c.muted }}>
          Figures marked with a period cover the {period.phrase}, against the same length of time
          before.
        </span>
        <div role="group" aria-label="Period" style={segTrack}>
          {PERIODS.map((p) => (
            <button
              key={p.days}
              type="button"
              aria-pressed={days === p.days}
              onClick={() => setDays(p.days)}
              style={segTab(days === p.days)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="gs-an">
        <Tile
          className="s3"
          dark
          label="Stock on hand"
          sub={`${stock.totalUnits} units, ${stock.productCount} product${stock.productCount === 1 ? '' : 's'}`}
          value={money(stock.totalValuePence)}
        >
          <Spark
            values={months.map((m) => m.stockValuePence)}
            names={monthNames}
            format={money}
            axisColor={c.onDarkFaint}
            color={c.onDark}
            fill="rgba(237,235,228,0.10)"
            label={`Stock value at the end of each of the last ${months.length} months`}
          />
        </Tile>
        <Tile
          className="s3"
          label="Bought"
          sub={`deliveries booked in, ${period.phrase}`}
          value={money(totals.bought.pence)}
          change={totals.bought.changePercent}
          moreIs="neutral"
        >
          <MiniBars
            values={months.map((m) => m.boughtPence)}
            names={monthNames}
            details={months.map((m) => shareLines(m.products.bought))}
            format={money}
            color={chart.greenPale}
            highlight={chart.navy}
            label="Bought each month"
          />
        </Tile>
        <Tile
          className="s3"
          label="Used and sold"
          sub={`at cost, ${period.phrase}`}
          value={money(totals.usedSold.pence)}
          change={totals.usedSold.changePercent}
          moreIs="good"
        >
          <MiniBars
            values={months.map((m) => m.usedPence + m.soldPence)}
            names={monthNames}
            details={months.map((m) => shareLines([...m.products.used, ...m.products.sold]))}
            format={money}
            color={chart.greenPale}
            highlight={chart.green}
            label="Used and sold each month, at cost"
          />
        </Tile>
        <Tile
          className="s3"
          label="Written off"
          sub={
            totals.wasteRatePercent === null
              ? period.phrase
              : `${totals.wasteRatePercent}% of what you bought`
          }
          value={money(totals.writtenOff.pence)}
          change={totals.writtenOff.changePercent}
          moreIs="bad"
        >
          <MiniBars
            values={months.map((m) => m.writtenOffPence)}
            names={monthNames}
            details={months.map((m) => shareLines(m.products.writtenOff))}
            format={money}
            color={chart.orangePale}
            highlight={chart.orange}
            label="Written off each month"
          />
        </Tile>

        <Panel
          className="s8"
          title="Where the stock money went"
          sub="Each month: what came in, what was used, what was sold and what was thrown away, all at cost"
        >
          {hasFlow ? (
            <>
              <div style={{ marginTop: 14 }}>
                <GroupedBars
                  labels={monthLabels}
                  names={monthNames}
                  details={months.map((m) => {
                    // The single biggest line of each kind, so the box says
                    // what the money was, without becoming a table.
                    const top = (label: string, shares: ProductShare[]) =>
                      shares[0]
                        ? [`Most ${label}: ${shares[0].productName} (${shares[0].units})`]
                        : [];
                    return [
                      ...top('bought', m.products.bought),
                      ...top('used', m.products.used),
                      ...top('sold', m.products.sold),
                      ...top('written off', m.products.writtenOff),
                    ];
                  })}
                  xLabel="Month"
                  yLabel="Cost of stock"
                  format={axisPounds}
                  label="Stock bought, used, sold and written off in each month"
                  series={[
                    { name: 'Bought', color: chart.navy, values: months.map((m) => m.boughtPence) },
                    {
                      name: 'Used in treatments',
                      color: chart.green,
                      values: months.map((m) => m.usedPence),
                    },
                    { name: 'Sold', color: chart.amber, values: months.map((m) => m.soldPence) },
                    {
                      name: 'Written off',
                      color: chart.orange,
                      values: months.map((m) => m.writtenOffPence),
                    },
                  ]}
                />
              </div>
              <Key
                items={[
                  ['Bought', chart.navy],
                  ['Used in treatments', chart.green],
                  ['Sold', chart.amber],
                  ['Written off', chart.orange],
                ]}
              />
            </>
          ) : (
            <p style={emptyNote}>
              Nothing has moved in the last {months.length} months. Book in a delivery or log some
              use and each month gets its bars.
            </p>
          )}
        </Panel>

        <Panel className="s4" title="Where your money is" sub="Stock value by type, today">
          {stock.byClassification.length === 0 ? (
            <p style={emptyNote}>Add stock and this shows how its value splits by type.</p>
          ) : (
            <>
              <SplitBar
                parts={stock.byClassification.map((row, i) => ({
                  label: row.label,
                  percent: row.sharePercent,
                  detail: `${money(row.valuePence)}, ${row.units} unit${row.units === 1 ? '' : 's'}`,
                  color: splitColors[i % splitColors.length]!,
                }))}
              />
              <div style={{ marginTop: 10 }}>
                {stock.byClassification.map((row, i) => (
                  <Line
                    key={row.label}
                    first={i === 0}
                    left={
                      <>
                        <Swatch color={splitColors[i % splitColors.length]!} />
                        {row.label}
                      </>
                    }
                    right={money(row.valuePence)}
                  />
                ))}
              </div>
            </>
          )}
        </Panel>
      </div>

      <Section>expiry and waste</Section>
      <div className="gs-an">
        <Panel
          className="s7"
          title="Going out of date, month by month"
          sub="Value of stock on the shelf that expires in each of the next six months"
        >
          {expiryTotal === 0 ? (
            <p style={emptyNote}>
              Nothing on the shelf has an expiry date in the next six months.
            </p>
          ) : (
            <>
              <div style={{ marginTop: 14 }}>
                <ColumnBars
                  xLabel="Month the stock expires"
                  yLabel="Value at cost"
                  format={axisPounds}
                  label="Value of stock expiring in each of the next six months"
                  bars={insights.expiry.months.map((m, i) => ({
                    label: shortMonth(m.monthKey),
                    name: `Expires in ${monthName(m.monthKey)}`,
                    lines: shareLines(m.products),
                    value: m.valuePence,
                    color: i === 0 ? chart.orange : i < 3 ? chart.amber : chart.green,
                  }))}
                />
              </div>
              {insights.expiry.expiredPence > 0 && (
                <p style={{ ...emptyNote, color: '#5A2511' }}>
                  On top of this, {money(insights.expiry.expiredPence)} of stock is already out of
                  date and still on the shelf.
                </p>
              )}
            </>
          )}
        </Panel>

        <Panel
          className="s5"
          title="What you wrote off, and why"
          sub={`At cost, ${period.phrase}`}
        >
          {insights.waste.byReason.length === 0 ? (
            <p style={emptyNote}>Nothing written off in the {period.phrase}.</p>
          ) : (
            <>
              <div style={{ marginTop: 10 }}>
                {insights.waste.byReason.map((r, i) => (
                  <Line
                    key={r.reason}
                    first={i === 0}
                    left={
                      <>
                        {r.label}
                        <Bar
                          percent={(r.pence / wasteMax) * 100}
                          color={[chart.orange, chart.amber, chart.navy, chart.grey][i % 4]!}
                        />
                      </>
                    }
                    right={money(r.pence)}
                  />
                ))}
              </div>
              {insights.waste.top && (
                <p style={emptyNote}>
                  Most written off: {insights.waste.top.productName},{' '}
                  {money(insights.waste.top.pence)}.
                </p>
              )}
            </>
          )}
        </Panel>
      </div>

      <Section>usage</Section>
      <div className="gs-an">
        <Panel className="s5" title="Most used" sub={`Units used or sold, ${period.phrase}`}>
          {hasUse ? (
            <div style={{ marginTop: 12 }}>
              <RankBars
                unit="units"
                xLabel="Units used or sold"
                label="Products ranked by units used or sold"
                rows={insights.mostUsed.map((m) => ({ name: m.productName, value: m.units }))}
              />
            </div>
          ) : (
            <p style={emptyNote}>
              Log use or a sale from any product and the busiest lines appear here.
            </p>
          )}
        </Panel>

        <Panel className="s3" title="Busiest days" sub="Units used or sold, by weekday">
          {hasUse ? (
            <>
              <div style={{ marginTop: 14 }}>
                <WeekdayBars
                  unit="units"
                  xLabel="Day of the week"
                  values={insights.weekdays}
                  busiest={insights.busiestWeekday}
                  label="Units used or sold on each day of the week"
                />
              </div>
              {insights.busiestWeekday !== null && (
                <p style={emptyNote}>
                  {WEEKDAY_NAMES[insights.busiestWeekday]} is your heaviest day.
                </p>
              )}
            </>
          ) : (
            <p style={emptyNote}>Nothing logged in the {period.phrase}.</p>
          )}
        </Panel>

        <Panel
          className="s4"
          title="Stock sitting still"
          sub={`On the shelf with nothing used or sold in the ${period.phrase}`}
        >
          {insights.sittingStill.products.length === 0 ? (
            <p style={emptyNote}>Nothing. Everything you hold has moved in the {period.phrase}.</p>
          ) : (
            <>
              <BigFigure
                value={money(insights.sittingStill.totalPence)}
                unit={`tied up in ${insights.sittingStill.products.length} product${
                  insights.sittingStill.products.length === 1 ? '' : 's'
                }`}
              />
              <div style={{ marginTop: 10 }}>
                {insights.sittingStill.products.slice(0, 5).map((p, i) => (
                  <Line
                    key={p.productId}
                    first={i === 0}
                    left={
                      <>
                        {p.productName}
                        <Small>
                          {p.qtyOnHand} on the shelf,{' '}
                          {p.lastMovedOn
                            ? `last used ${formatDate(p.lastMovedOn)}`
                            : 'never used or sold'}
                        </Small>
                      </>
                    }
                    right={money(p.valuePence)}
                  />
                ))}
              </div>
            </>
          )}
        </Panel>

        <Panel
          className="s12"
          title="How long your stock will last"
          sub={`From how fast each product has gone over the ${period.phrase}`}
        >
          {insights.cover.length === 0 ? (
            <p style={emptyNote}>
              Once you hold stock and have logged some use, this shows how many days each product
              will last and roughly when it runs out.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
                <thead>
                  <tr>
                    <th style={th}>product</th>
                    <th style={th}>on hand</th>
                    <th style={th}>used a week</th>
                    <th style={{ ...th, width: '24%' }}>cover</th>
                    <th style={th}>runs out around</th>
                    <th style={th}>minimum</th>
                    <th style={th}>status</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.cover.map((row, i) => (
                    <CoverLine key={row.productId} row={row} first={i === 0} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <Section>retail</Section>
      <div className="gs-an">
        <Panel
          className="s7"
          title="Retail sales"
          sub={`Revenue each ${insights.retailBucket}, ${period.phrase}`}
        >
          {retailSold ? (
            <>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 12 }}>
                <Figure label="Revenue" value={money(insights.retail.revenuePence)} />
                <Figure label="Profit" value={money(insights.retail.profitPence)} />
                <Figure
                  label="Margin"
                  value={
                    insights.retail.marginPercent === null
                      ? '—'
                      : `${insights.retail.marginPercent}%`
                  }
                />
                <Figure label="Units" value={String(insights.retail.units)} />
              </div>
              <div style={{ marginTop: 12 }}>
                <AreaLine
                  values={insights.retailSeries.map((p) => p.revenuePence)}
                  names={insights.retailSeries.map((p) =>
                    insights.retailBucket === 'month'
                      ? monthName(p.from.slice(0, 7))
                      : `Week of ${formatDate(p.from)}`,
                  )}
                  xLabel={insights.retailBucket === 'month' ? 'Month' : 'Week starting'}
                  yLabel="Revenue"
                  startLabel={
                    insights.retailBucket === 'month'
                      ? shortMonth(insights.retailSeries[0]?.from ?? '')
                      : shortDay(insights.retailSeries[0]?.from ?? '')
                  }
                  endLabel={
                    insights.retailBucket === 'month'
                      ? shortMonth(insights.retailSeries.at(-1)?.from ?? '')
                      : shortDay(insights.retailSeries.at(-1)?.from ?? '')
                  }
                  format={axisPounds}
                  label={`Retail revenue each ${insights.retailBucket}`}
                />
              </div>
            </>
          ) : (
            <p style={emptyNote}>
              Nothing sold in the {period.phrase}. Log a sale from Inventory and this fills in.
            </p>
          )}
        </Panel>

        <Panel
          className="s5"
          title="Best sellers"
          sub="By revenue, with the change on the period before"
        >
          {insights.bestSellers.length === 0 ? (
            <p style={emptyNote}>No sales in the {period.phrase}.</p>
          ) : (
            <div style={{ marginTop: 10 }}>
              {insights.bestSellers.map((b, i) => (
                <Line
                  key={b.productId}
                  first={i === 0}
                  left={
                    <>
                      {b.productName}
                      <Small>{b.units} sold</Small>
                    </>
                  }
                  right={
                    <>
                      {money(b.revenuePence)}{' '}
                      <Change
                        percent={b.changePercent}
                        moreIs="good"
                        fallback={b.previousRevenuePence === 0 ? 'new' : undefined}
                      />
                    </>
                  }
                />
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Section>stock counts and offers</Section>
      <div className="gs-an">
        <Panel
          className="s6"
          title="Stock count differences"
          sub={`Where a count did not match what the app expected, ${period.phrase}`}
        >
          {/* Emmanuel, 13 October: "what's in stock count difference, help me
              make sense of it". So the panel says what it is before it shows
              a number, names the products, and says what to do about it. */}
          <p style={{ ...emptyNote, marginTop: 10 }}>
            When you do a stock count, the app compares what you counted with what it thought
            you had. Anything that did not match is listed here. Missing stock usually means
            something was used or sold without being logged.
          </p>
          {insights.counts.lastOn === null ? (
            <p style={emptyNote}>
              No count has found a difference in the {period.phrase}. A count where everything
              matches changes nothing, so it does not appear here.
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 14 }}>
                <Figure label="Last difference found" value={formatDate(insights.counts.lastOn)} />
                <Figure
                  label="Missing from the shelf"
                  value={`${insights.counts.shortUnits} unit${
                    insights.counts.shortUnits === 1 ? '' : 's'
                  }`}
                />
                <Figure label="Worth, at cost" value={money(insights.counts.shortPence)} />
              </div>
              <div style={{ marginTop: 10 }}>
                {insights.counts.items.slice(0, 6).map((item, i) => (
                  <Line
                    key={item.productName + item.kind}
                    first={i === 0}
                    left={
                      <>
                        {item.productName}
                        <Small>
                          {item.kind === 'short'
                            ? `${item.units} fewer on the shelf than recorded`
                            : `${item.units} more on the shelf than recorded`}
                        </Small>
                      </>
                    }
                    right={
                      <span style={{ color: item.kind === 'short' ? c.danger : c.okText }}>
                        {item.kind === 'short' ? '-' : '+'}
                        {money(item.pence)}
                      </span>
                    }
                  />
                ))}
              </div>
              {insights.counts.foundUnits > 0 && (
                <p style={emptyNote}>
                  Counts also found {insights.counts.foundUnits} extra unit
                  {insights.counts.foundUnits === 1 ? '' : 's'} worth{' '}
                  {money(insights.counts.foundPence)}, usually a delivery that was never booked in.
                </p>
              )}
              <p style={emptyNote}>
                The reason given for each one is in the Audit log, under the stock count entry.
              </p>
            </>
          )}
        </Panel>

        <Panel
          className="s6"
          title="Did the promotions work"
          sub="Sold during each offer, and what the discount cost"
        >
          {promotions.results.length === 0 ? (
            <p style={emptyNote}>
              No promotions yet. Once one has run, this shows what it sold and what the discount
              cost you.
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 12 }}>
                <Figure label="Units on offer" value={String(promotions.totalUnits)} />
                <Figure label="Revenue" value={money(promotions.totalRevenuePence)} />
                <Figure label="Discount given" value={money(promotions.totalDiscountPence)} />
              </div>
              <div style={{ marginTop: 10 }}>
                {promotions.results.map((r, i) => (
                  <Line
                    key={r.promotionId}
                    first={i === 0}
                    left={
                      <>
                        {r.productName}
                        <Small>
                          {r.offer}, {r.unitsSold} sold
                        </Small>
                      </>
                    }
                    right={money(r.revenuePence)}
                  />
                ))}
              </div>
            </>
          )}
        </Panel>
      </div>

      {data.targetProgress && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', marginTop: 12 }}>
          <TargetProgress tp={data.targetProgress} profit={data.treatments} phrase={period.phrase} />
        </div>
      )}

      <style>{`
        .gs-an{display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:12px}
        .gs-an>.s3{grid-column:span 3}.gs-an>.s4{grid-column:span 4}.gs-an>.s5{grid-column:span 5}
        .gs-an>.s6{grid-column:span 6}.gs-an>.s7{grid-column:span 7}.gs-an>.s8{grid-column:span 8}
        .gs-an>.s12{grid-column:span 12}
        @media (max-width:1180px){
          .gs-an>.s3,.gs-an>.s4,.gs-an>.s5{grid-column:span 6}
          .gs-an>.s6,.gs-an>.s7,.gs-an>.s8{grid-column:span 12}
        }
        @media (max-width:700px){.gs-an>.s3,.gs-an>.s4,.gs-an>.s5{grid-column:span 12}}
      `}</style>
    </div>
  );
}

function CoverLine({ row, first }: { row: StockInsights['cover'][number]; first: boolean }) {
  const tag = COVER_TAG[row.status];
  // The bar is full at 60 days: beyond two months the exact length stops
  // mattering, and a longer scale would squash the rows that need attention.
  const percent = row.daysOfCover === null ? 100 : Math.min(100, (row.daysOfCover / 60) * 100);
  const cell = { ...td, borderTop: first ? 'none' : '1px solid #EFEFEA' };
  return (
    <tr>
      <td style={{ ...cell, fontWeight: 500 }}>{row.productName}</td>
      <td style={cell}>{row.qtyOnHand}</td>
      <td style={cell}>{row.perWeek}</td>
      <td style={cell}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              flex: 1,
              height: 6,
              borderRadius: 980,
              background: c.well,
              overflow: 'hidden',
              minWidth: 60,
            }}
          >
            <div
              style={{
                width: `${Math.max(percent, row.status === 'out' ? 0 : 3)}%`,
                height: '100%',
                borderRadius: 980,
                background: tag.bar,
              }}
            />
          </div>
          <span style={{ minWidth: 64, whiteSpace: 'nowrap' }}>
            {row.daysOfCover === null ? 'no use' : `${row.daysOfCover} days`}
          </span>
        </div>
      </td>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        {row.status === 'out'
          ? 'already out'
          : row.runsOutOn
            ? formatDate(row.runsOutOn)
            : 'not moving'}
      </td>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        {row.minLevel}
        {row.suggestedMinLevel !== null && row.suggestedMinLevel !== row.minLevel && (
          <span style={{ color: c.muted, fontSize: 13 }}>
            {' '}
            (two weeks of use is {row.suggestedMinLevel})
          </span>
        )}
      </td>
      <td style={cell}>
        <span
          style={{
            background: tag.bg,
            color: tag.fg,
            fontSize: 12.5,
            fontWeight: 500,
            borderRadius: 980,
            padding: '3px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          {tag.label}
        </span>
      </td>
    </tr>
  );
}

/**
 * design/Clinic Stock v2.dc.html:1453 — four figures, then treatments ranked by
 * what they brought in. Every number is computed by the API from the same
 * loader the Targets page uses; nothing here is derived in the browser beyond
 * formatting, so the two pages cannot drift.
 *
 * Spans the whole grid: it is a section of its own, not a fifth panel to be
 * squeezed beside the others.
 */
function TargetProgress({
  tp,
  profit,
  phrase,
}: {
  tp: NonNullable<AnalyticsResponse['targetProgress']>;
  profit: AnalyticsResponse['treatments'];
  /** "last 30 days": the period the profit panel covers. */
  phrase: string;
}) {
  const { progress, pace } = tp;
  const full: React.CSSProperties = { gridColumn: '1 / -1' };

  const heading = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '6px 0 12px' }}>
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: c.muted,
        }}
      >
        Target progress
      </span>
      <span style={{ flex: 1, height: 1, background: c.border }} />
      <span style={{ fontSize: 11.5, color: c.faint }}>{monthName(tp.monthKey)}</span>
    </div>
  );

  if (!tp.configured) {
    return (
      <div style={full}>
        {heading}
        <div style={{ ...cardStyle, marginBottom: 0 }}>
          <p style={{ ...emptyNote, margin: 0 }}>
            Add your monthly costs, a profit goal and your treatments in Targets, and this shows
            whether the month is on track.
          </p>
        </div>
      </div>
    );
  }

  const hasTarget = progress.targetPence > 0;
  // Retail sales count toward the target; say so when there are any.
  const retailNote =
    progress.retailSalesPence > 0 ? `, ${money(progress.retailSalesPence)} of it retail sales` : '';

  return (
    <div style={full}>
      {heading}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Stat
          label="Earned so far"
          value={money(progress.earnedPence)}
          sub={
            hasTarget
              ? `of ${money(progress.targetPence)} target (${tp.earnedPercentOfTarget}%)${retailNote}`
              : `no target set yet${retailNote}`
          }
        />
        <Stat
          label="Pace"
          value={
            pace.deltaPence === null
              ? '—'
              : `${pace.onPace ? '+' : '−'}${moneyRounded(Math.abs(pace.deltaPence))}`
          }
          sub={
            pace.deltaPence === null
              ? 'set a target to see your pace'
              : pace.onPace
                ? 'ahead of where you need to be'
                : `behind for day ${pace.dayOfMonth} of ${pace.daysInMonth}`
          }
          tone={pace.deltaPence === null ? undefined : pace.onPace ? c.success : c.danger}
        />
        <Stat
          label="Projected month-end"
          value={pace.projectedPence === null ? '—' : moneyRounded(pace.projectedPence)}
          sub={
            pace.projectedPence === null
              ? 'too early in the month to project'
              : !hasTarget
                ? 'at the rate so far'
                : pace.projectedHitsTarget
                  ? 'on course to hit target'
                  : 'short of target at this pace'
          }
          tone={
            pace.projectedPence === null || !hasTarget
              ? undefined
              : pace.projectedHitsTarget
                ? c.success
                : c.green
          }
        />
        <Stat
          label="Avg per treatment"
          value={
            tp.averagePerTreatmentPence === null ? '—' : moneyRounded(tp.averagePerTreatmentPence)
          }
          sub={`${progress.treatmentsDone} treatment${progress.treatmentsDone === 1 ? '' : 's'} logged`}
        />
      </div>

      <Panel title="Revenue by treatment" sub="Which treatments are carrying this month’s target">
        {tp.rows.length === 0 ? (
          <p style={emptyNote}>Add your treatments in Targets and they are ranked here.</p>
        ) : (
          <>
            <div style={{ height: 14 }} />
            {tp.rows.map((r) => (
              <div key={r.treatmentId} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    fontSize: 13.5,
                    marginBottom: 5,
                  }}
                >
                  <span>{r.name}</span>
                  <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>
                    {r.count} done · {money(r.pricePence)} each ·{' '}
                    <strong style={{ color: c.ink }}>{money(r.revenuePence)}</strong>
                  </span>
                </div>
                <div
                  style={{ height: 7, background: c.well, borderRadius: 980, overflow: 'hidden' }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${r.percentOfTop}%`,
                      background: c.green,
                      borderRadius: 980,
                    }}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      </Panel>

      <div style={{ height: 12 }} />
      <Panel title="Profit by treatment" sub={`After the products each one used, ${phrase}`}>
        {!profit || profit.rows.length === 0 ? (
          <p style={emptyNote}>
            Log a treatment from Targets and it shows here with what its products cost and what was
            left.
          </p>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                gap: 28,
                flexWrap: 'wrap',
                margin: '14px 0 18px',
              }}
            >
              <Figure label="Taken" value={money(profit.revenuePence)} />
              <Figure label="Products cost" value={money(profit.costPence)} />
              <Figure label="Left over" value={money(profit.profitPence)} />
              <Figure
                label="Margin"
                value={profit.marginPercent === null ? 'n/a' : `${profit.marginPercent}%`}
              />
            </div>
            {profit.rows.map((r) => (
              <div key={r.treatmentId} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    fontSize: 13.5,
                    marginBottom: 5,
                    flexWrap: 'wrap',
                  }}
                >
                  <span>
                    {r.name}
                    <span style={{ color: c.muted }}> · {r.count} done</span>
                  </span>
                  <span style={{ color: c.muted }}>
                    {money(r.revenuePence)} taken · {money(r.costPence)} products ·{' '}
                    <strong style={{ color: r.profitPence < 0 ? c.danger : c.ink }}>
                      {money(r.profitPence)} left
                    </strong>
                    {r.marginPercent !== null && ` (${r.marginPercent}%)`}
                  </span>
                </div>
                <div
                  style={{ height: 7, background: c.well, borderRadius: 980, overflow: 'hidden' }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${r.percentOfTop}%`,
                      background: chart.green,
                      borderRadius: 980,
                    }}
                  />
                </div>
              </div>
            ))}
            <p style={{ fontSize: 12.5, color: c.muted2, margin: '14px 0 0', lineHeight: 1.5 }}>
              Counts treatments logged from Targets, at the price and product cost on the day. Rent,
              wages and your other running costs are not taken off here.
            </p>
          </>
        )}
      </Panel>
    </div>
  );
}

function Panel({
  title,
  sub,
  className,
  children,
}: {
  title: string;
  sub?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className} style={{ ...cardStyle, padding: '20px 22px 22px', minWidth: 0 }}>
      <h2 style={{ fontWeight: 300, fontSize: 20, letterSpacing: '-0.03em', margin: 0 }}>
        {title}
      </h2>
      {sub && <div style={{ fontSize: 12.5, color: c.muted, marginTop: 2 }}>{sub}</div>}
      {children}
    </div>
  );
}

/** A headline figure with a small chart under it. */
function Tile({
  label,
  sub,
  value,
  change,
  moreIs = 'neutral',
  dark,
  className,
  children,
}: {
  label: string;
  sub: string;
  value: string;
  change?: number | null;
  moreIs?: 'good' | 'bad' | 'neutral';
  dark?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={className}
      style={{
        background: dark ? c.greenDeep : c.card,
        color: dark ? c.onDark : c.ink,
        borderRadius: 18,
        padding: '20px 22px 18px',
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 12.5, marginTop: 1, color: dark ? c.onDarkMuted : c.muted }}>
        {sub}
      </div>
      <div
        style={{
          fontSize: 32, fontWeight: 300,
          letterSpacing: '-1.1px',
          lineHeight: 1.1,
          marginTop: 12,
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        {value}
        {change !== undefined && <Change percent={change} moreIs={moreIs} />}
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

/**
 * "+12%" against the period before. Colour says whether that is welcome:
 * more written off is bad, more sold is good, and more bought is neither.
 * Nothing is shown when there was no previous period to compare with.
 */
function Change({
  percent,
  moreIs,
  fallback,
}: {
  percent: number | null;
  moreIs: 'good' | 'bad' | 'neutral';
  fallback?: string | undefined;
}) {
  if (percent === null && !fallback) return null;
  const up = (percent ?? 0) > 0;
  const flat = percent === null || percent === 0 || moreIs === 'neutral';
  const good = moreIs === 'good' ? up : !up;
  const tone = flat
    ? { background: c.well, color: c.muted2 }
    : good
      ? { background: c.okBg, color: c.okText }
      : { background: c.dangerBg, color: '#5A2511' };
  return (
    <span
      style={{
        ...tone,
        fontFamily: mono,
        fontSize: 12,
        letterSpacing: 0,
        borderRadius: 980,
        padding: '2px 6px',
        whiteSpace: 'nowrap',
      }}
    >
      {percent === null ? fallback : `${up ? '+' : ''}${percent}%`}
    </span>
  );
}

/** A figure card for target progress: label, large value, a line under it. */
function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  /** Explicit `| undefined` — the repo runs exactOptionalPropertyTypes. */
  tone?: string | undefined;
}) {
  return (
    <div style={{ background: c.card, borderRadius: 18, padding: '20px 22px 22px', minWidth: 0 }}>
      <div style={{ fontSize: 14.5, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 12.5, marginTop: 1, color: c.muted }}>{sub}</div>
      <div
        style={{
          fontSize: 30, fontWeight: 300,
          letterSpacing: '-1px',
          lineHeight: 1.1,
          marginTop: 12,
          color: tone ?? c.ink,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Section({ children }: { children: string }) {
  return (
    <div
      style={{
        fontSize: 13,
        fontWeight: 500,
        color: c.muted,
        margin: '22px 0 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {children.charAt(0).toUpperCase() + children.slice(1)}
      <span style={{ flex: 1, height: 1, background: c.border }} />
    </div>
  );
}

function Line({
  left,
  right,
  first,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
  first?: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 10,
        alignItems: 'center',
        padding: '9px 0',
        borderTop: first ? 'none' : '1px solid #EFEFEA',
        fontSize: 14.5,
      }}
    >
      <div style={{ minWidth: 0 }}>{left}</div>
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {right}
      </div>
    </div>
  );
}

function Small({ children }: { children: React.ReactNode }) {
  return <span style={{ display: 'block', fontSize: 12.5, color: c.muted }}>{children}</span>;
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 9,
        height: 9,
        borderRadius: 2,
        background: color,
        marginRight: 8,
      }}
    />
  );
}

function Bar({ percent, color }: { percent: number; color: string }) {
  return (
    <div
      style={{ height: 6, borderRadius: 980, background: c.well, overflow: 'hidden', marginTop: 6 }}
    >
      <div style={{ width: `${percent}%`, height: '100%', borderRadius: 980, background: color }} />
    </div>
  );
}

function Key({ items }: { items: [string, string][] }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        flexWrap: 'wrap',
        fontSize: 12.5,
        color: c.muted,
        marginTop: 10,
      }}
    >
      {items.map(([name, color]) => (
        <span key={name}>
          <Swatch color={color} />
          {name}
        </span>
      ))}
    </div>
  );
}

function BigFigure({ value, unit }: { value: string; unit: string }) {
  return (
    <div
      style={{
        fontSize: 30, fontWeight: 300,
        letterSpacing: '-1px',
        lineHeight: 1.1,
        marginTop: 12,
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      {value}
      <span style={{ fontSize: 14, letterSpacing: 0, color: c.muted }}>{unit}</span>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 12.5, color: c.muted }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 300, letterSpacing: '-0.6px', marginTop: 1 }}>{value}</div>
    </div>
  );
}
